import React, { useState, useEffect } from "react";
import RequestHandler from "util/RequestHandler";
import { defaultValidateMessages } from "./Helper";

import {
  Form,
  Col,
  Row,
  Button,
  Select,
  Input,
  InputNumber,
  Tooltip,
  Modal,
  DatePicker,
  Checkbox,
} from "antd";
import { CalculatorOutlined, PlusOutlined } from "@ant-design/icons";
import moment from "moment";

const formItemLayout = {
  labelCol: {
    xs: {
      span: 24,
    },
    sm: {
      span: 8,
    },
  },
  wrapperCol: {
    xs: {
      span: 24,
    },
    sm: {
      span: 16,
    },
  },
};

const commissionInputProps = {
  style: { width: "calc(100% - 35px)" },
  min: 0,
  max: 100,
  decimalSeparator: ",",
  precision: 2,
  placeholder: "-",
};

const PosCollectionCommissionDefinitionUpdateForm = (props) => {
  const [loading, setLoading] = useState(false);
  const [bankSelect, setBankSelect] = useState({
    data: [],
    loading: false,
  });
  const [calculateCommissionModal, setCalculateCommissionModal] = useState({
    visible: false,
    installmentCount: 2,
  });
  const [form] = Form.useForm();
  const [calculateForm] = Form.useForm();
  const [isUseCreditCardBinCheck, setIsUseCreditCardBinCheck] = useState(false);
  const [notAllowedChangeRatesBanks, setNotAllowedChangeRatesBanks] = useState([
    856, 857, 889, 890,
  ]);
  const [disableChangeRates, setDisableChangeRates] = useState(false);

  useEffect(() => {
    getBanks();
    RequestHandler.get("/TenantParameter/GetByTenant").then((response) => {
      if (response) {
        setIsUseCreditCardBinCheck(response.isUseCreditCardBinCheck);
      } else {
        setIsUseCreditCardBinCheck(false);
      }
    });
  }, []);

  useEffect(() => {
    if (props.data) {
      if (props.data.extraInstallmentExpireDate) {
        props.data.extraInstallmentExpireDate = moment(
          props.data.extraInstallmentExpireDate
        );
      }
      if (notAllowedChangeRatesBanks.includes(props.data.bankId)) {
        setDisableChangeRates(true);
      }

      form.setFieldsValue(props.data);
    }
  }, [props.data]);

  const getBanks = () => {
    setBankSelect({
      ...bankSelect,
      loading: true,
    });
    RequestHandler.get(
      `/PosCollectionCommissionDefinition/NotAddedBankInfos?$select=id,name,bankName`
    ).then((response) => {
      setBankSelect({
        data: response,
        loading: false,
      });
    });
  };

  const onFinish = (values) => {
    if (values.extraInstallmentCount === null) {
      values.extraInstallmentCount = 0;
    }

    RequestHandler.put(`/PosCollectionCommissionDefinition`, values)
      .then((response) => {
        setLoading(false);
        if (response?.success) {
          getBanks();
          form.resetFields();
          if (props.onSuccess) props.onSuccess();
        }
      })
      .catch(function (error) {
        setLoading(false);
      });
  };

  const renderInstallmentInput = (installmentCount) => {
    return (
      <Form.Item
        label={installmentCount + " Taksit"}
        style={{ marginBottom: 0 }}
        name={"commissionRate" + installmentCount}
      >
        <Input.Group compact>
          <Form.Item noStyle name={"commissionRate" + installmentCount}>
            <InputNumber
              {...commissionInputProps}
              name={"commissionRate" + installmentCount}
              style={{ width: "50%" }}
              disabled={disableChangeRates}
            />
          </Form.Item>
          <Tooltip title="Hesapla">
            <Button
              type="primary"
              icon={<CalculatorOutlined />}
              onClick={() =>
                setCalculateCommissionModal({
                  visible: true,
                  installmentCount: installmentCount,
                })
              }
            />
          </Tooltip>
          {isUseCreditCardBinCheck && (
            <Form.Item
              noStyle
              valuePropName="checked"
              name={"commissionRate" + installmentCount + "BusinessCardEnabled"}
            >
              <Checkbox
                style={{
                  marginLeft: 8,
                  paddingLeft: 4,
                  paddingRight: 8,
                  fontSize: 16,
                }}
                name={"ticariKart"}
              >
                Ticari Kart
              </Checkbox>
            </Form.Item>
          )}
        </Input.Group>
        <Input.Group compact style={{ paddingBottom: "20px" }} label="Limit">
          <Form.Item
            label="Alt Limit"
            name={"lowerLimitCommissionRate" + installmentCount}
          >
            <InputNumber
              style={{ width: "100%" }}
              decimalSeparator=","
              precision={2}
              min={0}
              placeholder="0,00"
              maxLength={15}
            />
          </Form.Item>

          <Form.Item
            label="Üst Limit"
            name={"upperLimitCommissionRate" + installmentCount}
          >
            <InputNumber
              style={{ width: "100%" }}
              decimalSeparator=","
              precision={2}
              min={0}
              placeholder="0,00"
              maxLength={15}
            />
          </Form.Item>
        </Input.Group>
      </Form.Item>
    );
  };

  const onCloseModal = () => {
    calculateForm.resetFields();
    setCalculateCommissionModal({
      ...calculateCommissionModal,
      visible: false,
    });
  };

  const onFinishCalculateForm = (values) => {
    const { installmentCount } = calculateCommissionModal;
    const firstCommissionRate = values.firstCommissionRate ?? 0;
    const otherCommissionRates = values.otherCommissionRates ?? 0;

    const formValues = {};
    formValues["commissionRate" + installmentCount] =
      (firstCommissionRate + (installmentCount - 1) * otherCommissionRates) /
      installmentCount;

    form.setFieldsValue(formValues);
    onCloseModal();
  };

  return (
    <>
      <Form
        {...formItemLayout}
        validateMessages={defaultValidateMessages}
        form={form}
        name="posCommissionUpdate"
        initialValues={{
          remember: true,
        }}
        onFinish={onFinish}
        onFinishFailed={() => setLoading(false)}
      >
        <Row>
          <Col span={24}>
            <Form.Item name="id" hidden>
              <Input />
            </Form.Item>

            <Form.Item
              name="posCollectionBankInfoId"
              label="Banka"
              rules={[{ required: true }]}
            >
              <Select
                allowClear
                showSearch
                placeholder="Seçiniz"
                filterOption={(input, option) =>
                  option.children
                    .toLocaleLowerCase("tr-TR")
                    .indexOf(input.toLocaleLowerCase("tr-TR")) >= 0
                }
                loading={bankSelect.loading}
              >
                {props.data?.posCollectionBankInfoId && (
                  <Select.Option
                    key={props.data.posCollectionBankInfoId}
                    value={props.data.posCollectionBankInfoId}
                    code={`${props.data.bankName} ${
                      props.data.posCollectionBankInfoName
                        ? `- ${props.data.posCollectionBankInfoName}`
                        : ""
                    }`}
                  >
                    {`${props.data.bankName} ${
                      props.data.posCollectionBankInfoName
                        ? `- ${props.data.posCollectionBankInfoName}`
                        : ""
                    }`}
                  </Select.Option>
                )}
                {bankSelect.data.map((item) => (
                  <Select.Option key={item.id} value={item.id}>
                    {`${item.bankName} ${item.name ? `- ${item.name}` : ""}`}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Artı Taksit Sayısı" name="extraInstallmentCount">
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                max={12}
                precision={0}
              />
            </Form.Item>

            <Form.Item
              label="Kampanya Bitiş Tarihi"
              name="extraInstallmentExpireDate"
            >
              <DatePicker
                format="DD.MM.YYYY"
                style={{ width: "100%" }}
                disabledDate={(current) => {
                  return moment().add(-1, "days") >= current;
                }}
              />
            </Form.Item>

            <Form.Item name="commissionRate1" label="Tek Çekim">
              <Input.Group compact>
                <Form.Item name="commissionRate1">
                  <InputNumber
                    {...commissionInputProps}
                    style={{ width: "100%" }}
                    disabled={disableChangeRates}
                  />
                </Form.Item>
                {isUseCreditCardBinCheck && (
                  <Form.Item
                    noStyle
                    valuePropName="checked"
                    name={"commissionRate1BusinessCardEnabled"}
                  >
                    <Checkbox
                      style={{
                        marginLeft: "14%",
                        paddingLeft: 4,
                        paddingRight: 8,
                        fontSize: 16,
                      }}
                      name={"ticariKart"}
                    >
                      Ticari Kart
                    </Checkbox>
                  </Form.Item>
                )}
              </Input.Group>
              <Input.Group compact style={{ paddingBottom: "20px" }}>
                <Form.Item label="Alt Limit" name="lowerLimitCommissionRate1">
                  <InputNumber
                    style={{ width: "100%" }}
                    decimalSeparator=","
                    precision={2}
                    min={0}
                    placeholder="0,00"
                    maxLength={12}
                  />
                </Form.Item>

                <Form.Item label="Üst Limit" name="upperLimitCommissionRate1">
                  <InputNumber
                    style={{ width: "100%" }}
                    decimalSeparator=","
                    precision={2}
                    min={0}
                    placeholder="0,00"
                    maxLength={12}
                  />
                </Form.Item>
              </Input.Group>
            </Form.Item>

            {renderInstallmentInput(2)}
            {renderInstallmentInput(3)}
            {renderInstallmentInput(4)}
            {renderInstallmentInput(5)}
            {renderInstallmentInput(6)}
            {renderInstallmentInput(7)}
            {renderInstallmentInput(8)}
            {renderInstallmentInput(9)}
            {renderInstallmentInput(10)}
            {renderInstallmentInput(11)}
            {renderInstallmentInput(12)}

            <Form.Item label="Açıklama" name="description">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row justify="space-between">
          <Col></Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              htmlType="submit"
              onClick={() => setLoading(true)}
              loading={loading}
            >
              Güncelle
            </Button>
          </Col>
        </Row>
      </Form>

      <Modal
        id="calculateCommissionModal"
        title={`${calculateCommissionModal.installmentCount} Taksit Komisyon Hesaplama`}
        centered
        visible={calculateCommissionModal.visible}
        onOk={onCloseModal}
        onCancel={onCloseModal}
        width={600}
        footer={null}
      >
        <Form
          {...formItemLayout}
          validateMessages={defaultValidateMessages}
          form={calculateForm}
          name="calculateForm"
          initialValues={{
            remember: true,
          }}
          onFinish={onFinishCalculateForm}
        >
          <Row>
            <Col span={24}>
              <Form.Item label="İlk Taksit" name="firstCommissionRate">
                <InputNumber {...commissionInputProps} placeholder="0,00" />
              </Form.Item>

              <Form.Item
                label="Sonraki Taksit(ler)"
                name="otherCommissionRates"
              >
                <InputNumber {...commissionInputProps} placeholder="0,00" />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="space-between">
            <Col></Col>
            <Col>
              <Button
                type="primary"
                icon={<CalculatorOutlined />}
                htmlType="submit"
              >
                Hesapla
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};
export default PosCollectionCommissionDefinitionUpdateForm;
